<template>
    <DCard
        :title="title"
        padding
    >
        <div class="d-flex justify-content-between gap-3">
            <DCol sm="6">
                <DFormRow :label="__('customers.business_incidents.id')">
                    {{ businessIncident.id }}
                </DFormRow>
                <DFormRow :label="__('customers.business_incidents.status')">
                    <DTag
                        v-if="businessIncident.status === 'solved'"
                        color="success"
                        :label="__('customers.business_incidents.statuses.' + businessIncident.status)"
                    />
                    <DTag
                        v-else
                        color="primary"
                        :label="__('customers.business_incidents.statuses.' + businessIncident.status)"
                    />
                </DFormRow>
                <DFormRow :label="__('customers.business_incidents.type')">
                    <DSelect
                        id="type"
                        v-model="form.incident_type_id"
                        :disabled="userPermissions.includes('customer.incidents_manage') === false || businessIncident.status === 'solved'"
                        :options="businessIncidentTypeOptions"
                        @change="getTypeData()"
                    />
                </DFormRow>
                <DFormRow :label="__('customers.business_incidents.customer_request')">
                    <DSelect
                        id="customerRequest"
                        v-model="form.customer_request_id"
                        :disabled="userPermissions.includes('customer.incidents_manage') === false || businessIncident.status === 'solved'"
                        :options="businessIncidentCustomerRequestOptions"
                    />
                </DFormRow>
                <DFormRow :label="__('customers.business_incidents.incident_solution')">
                    <DSelect
                        id="incidentSolution"
                        v-model="form.incident_solution_id"
                        :disabled="userPermissions.includes('customer.incidents_manage') === false || businessIncident.status === 'solved'"
                        :options="businessIncidentSolutionOptions"
                    />
                </DFormRow>
            </DCol>
            <DCol
                sm="6"
                class="note-section"
            >
                <DFormRow
                    :label="__('customers.business_incidents.notes')"
                    is-vertical
                    class="pr-5"
                >
                    <DFormTextarea
                        id="note"
                        v-model="form.notes"
                        :disabled="userPermissions.includes('customer.incidents_manage') === false || businessIncident.status === 'solved'"
                        rows="5"
                        style="padding-right: 20px"
                    />
                </DFormRow>
            </DCol>
        </div>
    </DCard>
    <DFabs>
        <DFab
            v-if="userPermissions.includes('customer.incidents_manage') && businessIncident.status === 'open' && form.incident_type_id !== null && form.customer_request_id !== null && form.incident_solution_id !== null"
            id="btn-solve"
            icon="check-circle"
            :label="__('customers.business_incidents.solve_incident')"
            @click="solveIncident"
        />
        <DFab
            v-if="userPermissions.includes('customer.incidents_manage') && businessIncident.status === 'open'"
            id="btn-save"
            :icon="'content-save'"
            :label="__('messages.save')"
            @click="submitForm"
        />
        <DFab
            v-if="businessIncident.id !== null && userPermissions.includes('customer.incidents_delete')"
            id="btn-delete"
            :icon="'trash-can'"
            :label="__('messages.delete')"
            color="danger"
            @click="deleteIncident"
        />
        <DFab
            icon="close"
            color="secondary"
            @click="cancelRoute"
        />
    </DFabs>
    <ConnectedContracts
        :items="businessIncidentContracts"
        :show-card="showCardConnectedContracts"
        :incident="businessIncident"
        @add-contracts="addContracts"
    />
    <ConnectedInvoices
        :items="businessIncidentInvoices"
        :show-card="showCardConnectedInvoices"
        :incident="businessIncident"
        :customer="customer"
        @add-invoices="addInvoices"
    />
    <ConnectedOrders
        :items="businessIncidentOrders"
        :show-card="showCardConnectedOrders"
        :incident="businessIncident"
        @add-orders="addOrders"
    />
    <ConnectedTickets
        :items="businessIncidentTickets"
        :show-card="showCardConnectedTickets"
        :incident="businessIncident"
        @add-tickets="addTickets"
    />
    <ConnectedActivityLogs
        :items="businessIncidentActivityLogs"
        :show-card="showCardConnectedActivityLogs"
        :incident="businessIncident"
    />
    <DModal
        v-model:show="showConfirmationModal"
        :title="__('customers.business_incidents.delete_confirmation.title')"
        :message="__('customers.business_incidents.delete_confirmation.message')"
        is-confirm
        is-error
        @close="showConfirmationModal = false"
        @confirm="deleteCallBack"
    />
</template>

<script setup>
import { router, useForm, usePage } from '@inertiajs/vue3'
import { computed, onMounted, ref } from 'vue'

import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import ConnectedContracts from '~@/Components/Customers/BusinessIncidents/ConnectedContracts'
import ConnectedInvoices from '~@/Components/Customers/BusinessIncidents/ConnectedInvoices'
import ConnectedOrders from '~@/Components/Customers/BusinessIncidents/ConnectedOrders'
import ConnectedTickets from '~@/Components/Customers/BusinessIncidents/ConnectedTickets'
import ConnectedActivityLogs from '~@/Components/Customers/BusinessIncidents/ConnectedActivityLogs'

const props = defineProps({
    errors: {
        type: Object,
        default: () => { return {} }
    },
    businessIncident: {
        type: Object,
        default: () => { return {} }
    },
    businessIncidentTypes: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentCustomerRequests: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentSolutions: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentContracts: {
        type: Array,
        default () {
            return []
        }
    },
    businessIncidentInvoices: {
        type: Array,
        default () {
            return []
        }
    },
    businessIncidentOrders: {
        type: Array,
        default () {
            return []
        }
    },
    businessIncidentTickets: {
        type: Array,
        default () {
            return []
        }
    },
    businessIncidentActivityLogs: {
        type: Array,
        default () {
            return []
        }
    },
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const emit = defineEmits([
    'submitForm',
    'solveIncident',
    'cancel',
    'addContracts',
    'addInvoices',
    'addOrders',
    'addTickets'
])

const title = computed(() => {
    if (props.businessIncident.id !== null) {
        return __('customers.business_incidents.incident_detail')
    }

    return __('customers.business_incidents.incident_create')
})

const userPermissions = computed(() => usePage().props.auth.permissions)

const businessIncidentTypeOptions = computed(function () {
    const data = [{
        value: null,
        label: ''
    }]

    let inactiveItem = true

    for (const item of props.businessIncidentTypes) {
        data.push({ value: item.value, label: item.text })

        if (props.businessIncident.incident_type_id === item.value) {
            inactiveItem = false
        }
    }

    if (inactiveItem === true && props.businessIncident.incident_type_id !== null) {
        data.push({ value: props.businessIncident.business_incident_type.id, label: props.businessIncident.business_incident_type.name })
    }

    return data
})

const businessIncidentCustomerRequestOptions = ref([])
const businessIncidentSolutionOptions = ref([])
const showCardConnectedContracts = ref(true)
const showCardConnectedInvoices = ref(true)
const showCardConnectedOrders = ref(true)
const showCardConnectedTickets = ref(true)
const showCardConnectedActivityLogs = ref(true)
const showConfirmationModal = ref(false)

onMounted(() => {
    getCustomerRequests(props.businessIncidentCustomerRequests)
    getIncidentSolutions(props.businessIncidentSolutions)
})

const form = useForm({
    status: props.businessIncident.status,
    notes: props.businessIncident.notes,
    incident_type_id: props.businessIncident.incident_type_id,
    customer_request_id: props.businessIncident.customer_request_id,
    incident_solution_id: props.businessIncident.incident_solution_id
})

function getTypeData () {
    if (form.incident_type_id === null || form.incident_type_id === '') {
        form.customer_request_id = null
        form.incident_solution_id = null
        businessIncidentCustomerRequestOptions.value = []
        businessIncidentSolutionOptions.value = []

        return
    }
    window.axios.get(route('customers.customers.fetch-business-incident-data', form.incident_type_id))
        .then(response => {
            const data = response.data
            form.customer_request_id = null
            form.incident_solution_id = null
            getCustomerRequests(data.customerRequests)
            getIncidentSolutions(data.incidentSolutions)
        })
}

function getCustomerRequests (customerRequests) {
    const data = [{
        value: null,
        label: ''
    }]

    let inactiveItem = true

    for (const item of customerRequests) {
        data.push({ value: item.value, label: item.text })

        if (props.businessIncident.customer_request_id === item.value) {
            inactiveItem = false
        }
    }

    if (inactiveItem === true && form.customer_request_id !== null) {
        data.push({ value: props.businessIncident.business_incident_customer_request.id, label: props.businessIncident.business_incident_customer_request.name })
    }

    businessIncidentCustomerRequestOptions.value = data
}

function getIncidentSolutions (incidentSolutions) {
    const data = [{
        value: null,
        label: ''
    }]

    let inactiveItem = true

    for (const item of incidentSolutions) {
        data.push({ value: item.value, label: item.text })

        if (props.businessIncident.incident_solution_id === item.value) {
            inactiveItem = false
        }
    }

    if (inactiveItem === true && form.incident_solution_id !== null) {
        data.push({ value: props.businessIncident.business_incident_solution.id, label: props.businessIncident.business_incident_solution.name })
    }

    businessIncidentSolutionOptions.value = data
}

function submitForm () {
    emit('submitForm', form)
}

function solveIncident () {
    form.status = 'solved'

    emit('solveIncident', form)
}

function addContracts (contractsForm) {
    emit('addContracts', {
        incidentForm: form,
        contractsForm
    })
}

function addInvoices (invoicesForm) {
    emit('addInvoices', {
        incidentForm: form,
        invoicesForm
    })
}

function addOrders (ordersForm) {
    emit('addOrders', {
        incidentForm: form,
        ordersForm
    })
}

function addTickets (ticketsForm) {
    emit('addTickets', {
        incidentForm: form,
        ticketsForm
    })
}

function cancelRoute () {
    emit('cancel')
}

function deleteIncident () {
    showConfirmationModal.value = true
}

function deleteCallBack () {
    router.delete(route('customers.customers.business-incident.destroy', {
        customer: props.customer?.id ?? null,
        businessIncident: props.businessIncident.id
    }))
    showConfirmationModal.value = false
}

</script>

<style lang="scss" scoped>
.note-section {
    padding-right: 20px;
}
</style>
